// Small presentational primitives: Chip, Tabs, PageHeader, Skeleton, ErrorBox, EmptyState, Sheet
import type { ComponentChildren, JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { cx } from '../lib/format';
import { IcBack, IcClose } from './icons';

/* ---------- Chip ---------- */
interface ChipProps {
  children: ComponentChildren;
  on?: boolean;
  onClick?: () => void;
  href?: string;
  tone?: 'soft' | 'ball' | 'green' | 'red' | 'plain';
  size?: 'sm';
  class?: string;
  title?: string;
}
export function Chip({ children, on, onClick, href, tone, size, class: cls, title }: ChipProps) {
  const c = cx('chip', on && 'on', tone, size, cls);
  if (onClick) {
    return (
      <button type="button" class={c} aria-pressed={on ? 'true' : 'false'} onClick={onClick} title={title}>
        {children}
      </button>
    );
  }
  if (href) {
    return (
      <a class={c} href={href} title={title}>
        {children}
      </a>
    );
  }
  return (
    <span class={c} title={title}>
      {children}
    </span>
  );
}

/* ---------- Tabs ---------- */
export interface TabDef {
  key: string;
  label: ComponentChildren;
  href?: string;
  count?: number;
}
export function Tabs({ tabs, active, onChange, seg }: { tabs: TabDef[]; active: string; onChange?: (key: string) => void; seg?: boolean }) {
  return (
    <nav class={cx('tabs', seg && 'seg')} role="tablist">
      {tabs.map((t) =>
        t.href ? (
          <a key={t.key} href={t.href} class={cx(t.key === active && 'on')} role="tab" aria-selected={t.key === active ? 'true' : 'false'}>
            {t.label}
            {t.count != null && t.count > 0 && <span class="countbubble">{t.count}</span>}
          </a>
        ) : (
          <button
            key={t.key}
            type="button"
            class={cx(t.key === active && 'on')}
            role="tab"
            aria-selected={t.key === active ? 'true' : 'false'}
            onClick={() => onChange?.(t.key)}
          >
            {t.label}
            {t.count != null && t.count > 0 && <span class="countbubble">{t.count}</span>}
          </button>
        ),
      )}
    </nav>
  );
}

/* ---------- PageHeader ---------- */
export function PageHeader({ title, sub, back, actions }: { title: ComponentChildren; sub?: ComponentChildren; back?: boolean | string; actions?: ComponentChildren }) {
  const { back: goBack, route } = useLocation();
  return (
    <header class="page-header">
      {back && (
        <button
          type="button"
          class="backbtn"
          aria-label="ย้อนกลับ"
          onClick={() => {
            if (typeof back === 'string') route(back);
            else if (history.length > 1) goBack();
            else route('/');
          }}
        >
          <IcBack size={22} />
        </button>
      )}
      <div class="grow">
        <h1>{title}</h1>
        {sub && <div class="sub">{sub}</div>}
      </div>
      {actions}
    </header>
  );
}

/* ---------- Skeleton ---------- */
export function Skeleton({ h = 14, w, r, class: cls, style }: { h?: number | string; w?: number | string; r?: number; class?: string; style?: JSX.CSSProperties }) {
  return <div class={cx('skeleton', cls)} style={{ height: h, width: w ?? '100%', borderRadius: r, ...style }} aria-hidden="true" />;
}
export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  return (
    <div class="card" aria-busy="true">
      <div class="row">
        <Skeleton h={44} w={44} r={22} />
        <div class="grow stack-8">
          <Skeleton h={16} w="70%" />
          {Array.from({ length: lines - 1 }).map((_, i) => (
            <Skeleton key={i} h={12} w={`${50 - i * 10}%`} />
          ))}
        </div>
      </div>
    </div>
  );
}
export function SkeletonList({ n = 3, lines }: { n?: number; lines?: number }) {
  return (
    <div class="stack">
      {Array.from({ length: n }).map((_, i) => (
        <SkeletonCard key={i} lines={lines} />
      ))}
    </div>
  );
}

/* ---------- Error ---------- */
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div class="errbox" role="alert">
      <span>{message}</span>
      {onRetry && (
        <button type="button" class="btn sm secondary" onClick={onRetry}>
          ลองใหม่
        </button>
      )}
    </div>
  );
}

/* ---------- Empty state with court-line motif ---------- */
export function CourtLines() {
  return (
    <svg viewBox="0 0 120 72" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <rect x="6" y="8" width="108" height="56" rx="3" />
      <path d="M60 8v56" stroke-dasharray="3 3" />
      <path d="M6 36h108" />
      <path d="M33 8v56M87 8v56" />
      <circle cx="96" cy="22" r="4" fill="currentColor" stroke="none" opacity=".8" />
    </svg>
  );
}
export function EmptyState({ title, desc, action, icon }: { title: string; desc?: ComponentChildren; action?: ComponentChildren; icon?: ComponentChildren }) {
  return (
    <div class="empty">
      {icon ?? <CourtLines />}
      <div class="t">{title}</div>
      {desc && <div class="d">{desc}</div>}
      {action}
    </div>
  );
}

/* ---------- Sheet ---------- */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title?: ComponentChildren; children: ComponentChildren }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div class="sheet-back" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="grip" />
        <div class="row between mb-8">
          {title ? <h2>{title}</h2> : <span />}
          <button type="button" class="iconbtn" aria-label="ปิด" onClick={onClose}>
            <IcClose />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Spinner ---------- */
export function Spinner() {
  return <span class="spinner" aria-label="กำลังโหลด" />;
}
